<template>
    <div>
        <modal-dialog
            :id="`new-contact-${id}`"
            :show.sync="shown"
        >
            <template v-slot:title>
                Create a New Contact
            </template>

            <form
                v-if="!!shell_record"
                class="uk-form"
                @submit.prevent
            >
                <div class="uk-flex uk-margin-bottom uk-width-1-1">
                    <div class="uk-width-1-2">
                        <form-input
                            v-if="pickerMode"
                            :id="`new-contact-first-name-${id}`"
                            v-model="shell_record.first_name"
                            name="first_name"
                            :required="true"
                            :editable="!shell_record.id"
                            placeholder="First Name"
                            width="1-1"
                        />
                        <form-sourced-autosuggest
                            v-else
                            entity="contact"
                            :id="`new-contact-first-name-${id}`"
                            :selection="shell_record.first_name"
                            name="first_name"
                            placeholder="First Name"
                            :required="true"
                            :editable="true"
                            :can-add="true"
                            :add-handler="contact_picked"
                            @pick="contact_picked"
                        >
                            <template v-slot:option="{ option }">
                                {{ option.name }}
                                <div><small>{{ option.firm_name }}</small></div>
                            </template>
                        </form-sourced-autosuggest>
                    </div>
                    <div class="uk-margin-small-left uk-width-1-2">
                        <form-input
                            :id="`new-contact-last-name-${id}`"
                            v-model="shell_record.last_name"
                            name="last_name"
                            :required="true"
                            :editable="!pickerMode || !shell_record.id"
                            placeholder="Last Name"
                            width="1-1"
                        />
                    </div>
                    <div v-if="!profile.google_api_oauth_url" class="uk-margin-small-left uk-flex-item-none">
                        <button
                            type="button"
                            title="Import from your Google Contacts..."
                            class="uk-button uk-button-link"
                            @click.prevent="google_search"
                        >
                            <img src="/img/google-logo.png" style="height: 1em;" />
                        </button>
                    </div>
                </div>
                <div class="uk-grid uk-grid-small" uk-data-grid-margins>
                    <div class="uk-width-1-1">
                        <firm-picker
                            :id="`new-contact-firm-id-${id}`"
                            :value="shell_record.firm_id"
                            :external-search="(!!shell_record._google_contact && !shell_record.firm_id) ? shell_record.firm_name : ''"
                            placeholder="Firm"
                            :editable="!pickerMode"
                            @pick="firm_picked"
                        />
                    </div>
                </div>
                <div class="uk-grid uk-grid-small" uk-data-grid-margins>
                    <div class="uk-width-1-1">
                        <form-input
                            :id="`new-contact-city-state-${id}`"
                            v-model="shell_record.city_state"
                            name="city_state"
                            :required="false"
                            :editable="!pickerMode || !shell_record.id"
                            placeholder="City, State, Country"
                            width="1-1"
                        />
                    </div>
                </div>
                <div class="uk-grid uk-grid-small" uk-data-grid-margins>
                    <div class="uk-width-1-1">
                        <form-input
                            :id="`new-contact-title-${id}`"
                            v-model="shell_record.title"
                            name="title"
                            :required="true"
                            :editable="!pickerMode || !shell_record.id"
                            placeholder="Title"
                            width="1-1"
                        />
                    </div>
                </div>
                <div class="uk-grid uk-grid-small" uk-data-grid-margins>
                    <div class="uk-width-1-1">
                        <form-tags-input
                            :id="`new-contact-position-filters-${id}`"
                            name="position_filters"
                            v-model="shell_record.position_filter_ids"
                            :local-options="position_filters"
                            placeholder="Position Filter(s)"
                            :required="false"
                            :editable="!pickerMode || !shell_record.id"
                            class="uk-width-1-1"
                        />
                    </div>
                </div>
                <div class="uk-grid uk-grid-small" uk-data-grid-margins>
                    <div class="uk-width-1-1">
                        <form-input
                            :id="`new-contact-email-${id}`"
                            v-model="shell_record.email"
                            name="email"
                            :required="false"
                            :editable="true"
                            placeholder="Email"
                            width="1-1"
                        />
                    </div>
                </div>
                <div class="uk-modal-footer uk-text-center">
                    <base-button
                        size="large"
                        @click.prevent="close"
                    >
                        Cancel
                    </base-button>
                    <base-button
                        color="primary"
                        size="large"
                        @click.prevent="create"
                    >
                        <span v-if="pickerMode && !!shell_record.id">Select</span>
                        <span v-else-if="!pickerMode && !!shell_record.id">View Existing</span>
                        <span v-else>Create</span>
                    </base-button>
                </div>
            </form>
        </modal-dialog>
        <modal-dialog
            id="google-contact-query-dialog"
            :show.sync="querying"
        >
            <template v-slot:title>
                Find a Google Contact
            </template>

            <form
                class="uk-form"
                @submit.prevent
            >
                <form-sourced-autosuggest
                    entity="contact"
                    id="google-contact-query"
                    :selection="google_contact.id"
                    name="google_query"
                    placeholder="Name, Nickname, email, phone or organization"
                    :required="true"
                    :editable="true"
                    :can-add="false"
                    @pick="google_contact_picked"
                >
                    <template v-slot:option="{ option }">
                        {{ option.name }}
                        <div><small>{{ option.firm_name }}</small></div>
                    </template>
                </form-sourced-autosuggest>

                <div class="uk-modal-footer uk-text-center">
                    <base-button
                        @click.prevent="querying = false"
                    >
                        Cancel
                    </base-button>
                    <base-button
                        color="primary"
                        :disabled="!google_contact.id"
                        @click.prevent="google_contact_import"
                    >
                        Import<span v-if="!!google_contact.name"> {{ google_contact.name }}</span>
                    </base-button>
                </div>
            </form>
        </modal-dialog>
    </div>
</template>

<script type="text/javascript">
import debounce from "lodash/debounce"

import string_utilities from "../mixins/StringUtilities"
import profile_utilities from "../mixins/ProfileUtilities"

import ModalDialog from "./ModalDialog/ModalDialog"
import FormSourcedAutosuggest from "../ui/FormSourcedAutosuggest"
import FirmPicker from "../ui/FirmPicker"
import FormInput from "../ui/FormInput"
import FormTagsInput from "../ui/FormTagsInput"
import BaseButton from "../ui/BaseButton"

/*
ON PICK existing contact, set this.shell_record.id ; if !!this.shell_record.id, show button to view contact editor screen
Firm picker ONLY if !this.shell_record.id
NO autosave here, even is !!this.shell_record.id: always save/create ONLY on "Create" button clicked
*/

export default {
    mixins: [string_utilities, profile_utilities],
    components: {
        ModalDialog,
        FormSourcedAutosuggest,
        FirmPicker,
        FormInput,
        FormTagsInput,
        BaseButton
    },
    mounted () {
        this.reset_data()
    },
    watch: {
        show: "reset_data",
        prefill: "prefilled",
        query: "debounce_query"
    },
    methods: {
        reset_data () {
            this.creating = false
            if(this.show) {
                this.$store.dispatch(
                    "FETCH_RECORD_SHELL",
                    {
                        entity: "contact"
                    }
                ).then(record => {
                    this.shell_record = { ...record }
                    this.prefilled()
                }).catch(error => {
                    this.$error(error.message)
                })
            }
        },
        prefilled () {
            if(this.prefill) {
                Object.keys(this.prefill).forEach(field => {
                    this.shell_record[field] = this.prefill[field]
                })
            }
        },
        contact_picked (contact) {
            if(!contact) return
            this.$delete(this.shell_record, "_google_contact")
            if(!contact.id) {
                this.shell_record.id = 0
                const name_tokens = contact.data.name.match(/^([^\s]+)(?:[\s]+(.*?)?)?$/)
                this.shell_record.first_name = (name_tokens.length ? name_tokens[1] : "")
                this.shell_record.last_name = ((name_tokens.length > 2) ? name_tokens[2] : "")
                this.shell_record.email = ""
                this.shell_record.title = ""
                this.shell_record.city_state = ""
                this.shell_record.firm_id = 0
                this.shell_record.firm_name = ""
                this.shell_record.position_filter_ids = []
            } else {
                this.shell_record.id = contact.id
                this.shell_record.first_name = contact.first_name
                this.shell_record.last_name = contact.last_name
                this.shell_record.email = contact.email
                this.shell_record.title = contact.title
                this.shell_record.city_state = contact.city_state
                this.shell_record.firm_id = contact.firm_id
                this.shell_record.firm_name = contact.firm_name
                this.shell_record.position_filter_ids = contact.position_filter_ids || []
            }
        },
        firm_picked (firm) {
            this.shell_record.firm_id = firm.id
            if(!this.shell_record.city_state) this.shell_record.city_state = firm.city_state
        },
        create () {
            if(this.pickerMode && !!this.shell_record.id) {
                this.$emit("created", this.shell_record)
                this.close()
            } else if(!this.pickerMode && !!this.shell_record.id) {
                this.$router.push({
                    name: "contact.edit",
                    params: { id: this.shell_record.id }
                })
                this.close()
            } else {
                this.$store.dispatch("CREATE_RECORD", {
                    entity: "contact",
                    data: this.shell_record
                }).then(record => {
                    this.$emit("created", record)
                    this.close()
                })
            }
        },
        close () {
            this.$emit("update:show", false)
        },
        google_search () {
            this.google_contact = { id: 0 }
            this.query = this.nibnut_filter("nibnut.concat", [[this.shell_record.first_name, this.shell_record.last_name]])
            this.querying = true
        },
        debounce_query: debounce(function () {
            if(this.shown && !!this.query && (this.query.length >= 3)) {
                console.log("DEBUG: QUERY")
            }
        }, 500),
        google_contact_picked (google_contact) {
            // console.log(google_contact)
            this.google_contact = google_contact
        },
        google_contact_import () {
            if(this.google_contact && this.google_contact.id) {
                this.shell_record.id = 0
                this.shell_record.first_name = this.google_contact.first_name
                this.shell_record.last_name = this.google_contact.last_name
                this.shell_record.email = this.google_contact.email
                this.shell_record.title = this.google_contact.title
                this.shell_record.city_state = this.google_contact.city_state
                this.shell_record.firm_id = 0
                this.shell_record.firm_name = this.google_contact.firm_name
                this.shell_record.position_filter_ids = []
                this.$set(this.shell_record, "_google_contact", this.google_contact.raw)
                this.querying = false
            }
        }
    },
    computed: {
        shown: {
            get: function () {
                return this.show
            },
            set: function (show) {
                if(!show) this.close()
            }
        },
        position_filters () {
            return this.dictionary("position_filter", "name")
        }
    },
    props: {
        show: {
            type: Boolean,
            default: false
        },
        id: {
            type: String,
            default: ""
        },
        prefill: {
            type: Object,
            default () {
                return {}
            }
        },
        pickerMode: {
            type: Boolean,
            default: false
        }
    },
    data () {
        return {
            shell_record: null,

            creating: false,

            query: "",
            querying: false,
            google_contact: { id: 0 },
            fetching: false
        }
    }
}
</script>
